<script lang="ts">
	import {
		getDeviceSettings,
		readDeviceSettings,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
	import {
		get_gen_2_component_channel_structure,
		real_id,
	} from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { Toggle, Alert } from '@Shelly/shelly-design-system';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	const id = real_id(device.id);
	const component = get_gen_2_component_channel_structure(device);

	let enabled = false;
	let cacheEnabled = false;
	let loading = false;

	$: status = devices_status.getByRealId(device.id);
	$: awayModeActive = status?.[component]?.away_mode || false;

	$: if (!loading && $settingsStore) {
		const root = $settingsStore;
		const v = root[component]?.auto_heating_on === true;
		if (v !== cacheEnabled) {
			enabled = v;
			cacheEnabled = v;
		}
	}

	const handleToggle = async () => {
		const newEnabled = !enabled;
		if (cacheEnabled === newEnabled) {
			return;
		}

		loading = true;
		try {
			const res: SettingRequest = await gen2Request(device, 'Cury.SetConfig', {
				id: 0,
				config: { auto_heating_on: newEnabled },
			});
			onSettingsResponse(res, device);
			if (res?.isok) {
				const root = readDeviceSettings(id);
				const updated = {
					...root,
					[component]: {
						...root?.[component],
						auto_heating_on: newEnabled,
					},
				};
				devices_settings.update_devices_settings(id, updated);
				enabled = newEnabled;
				cacheEnabled = newEnabled;
			} else {
				enabled = cacheEnabled;
			}
		} catch (err) {
			console.error('Cury.SetConfig auto_heating_on failed:', err);
			enabled = cacheEnabled;
		} finally {
			loading = false;
		}
	};
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{:else}
	{#if !$showNewLayout}
		<Alert
			class="mb-s"
			type="info"
			heading={$format('label_vial_auto_switching_description', {
				default: 'Automatically turn one of the slots heating on if the current vial is empty',
			})}
		/>
	{/if}
	<Toggle
		id="toggle-cury-vial-auto-switching"
		class="mb-s"
		size="m"
		checked={enabled}
		disabled={loading}
		on:click={handleToggle}
		info={$showNewLayout
			? $format('label_vial_auto_switching_description', {
					default:
						'Automatically turn one of the slots heating on if the current vial is empty',
				})
			: undefined}
	>
		{$format('label_enable', {
			default: 'Enable',
		})}
	</Toggle>
{/if}
